/**
 * StickyFormLayout — layout con navegación lateral sticky + secciones.
 *
 * Portado del EventEditor de kpop para dar consistencia a los tres
 * editores de la plataforma (IES shows, cine sessions, pagafacil shows).
 *
 * Uso:
 *   <StickyFormLayout
 *     title="Editar evento"
 *     sections={[
 *       { id: 'general', icon: '📋', label: 'General', content: <> ... </> },
 *       ...
 *     ]}
 *     footerActions={<button onClick={save}>Guardar</button>}
 *   />
 *
 * Comportamiento:
 * - Sidebar sticky a la izquierda (colapsa en móvil arriba con scroll H)
 * - Scroll spy: al hacer scroll en el contenido, resalta el link activo
 * - Click en un link hace scrollIntoView smooth al ancla
 * - Botón "Guardar" fijo debajo del sidebar (siempre visible)
 */
import { useEffect, useRef, useState } from 'react';

export interface StickyFormSection {
  id: string;
  label: string;
  icon?: string;
  content: React.ReactNode;
  // Opcional: badge/chip a la derecha del label (ej. "NEW", "3 avisos")
  badge?: React.ReactNode;
}

interface Props {
  title: string;
  subtitle?: React.ReactNode;
  sections: StickyFormSection[];
  footerActions: React.ReactNode;
  // Alto máximo del área scrollable — permite adaptar a modal o página
  maxHeight?: string;
  className?: string;
}

export default function StickyFormLayout({
  title, subtitle, sections, footerActions, maxHeight = 'calc(100vh - 100px)', className = '',
}: Props) {
  const [active, setActive] = useState(sections[0]?.id || '');
  const mainRef = useRef<HTMLDivElement>(null);

  // Scroll spy simple: la sección cuyo top esté más cerca del top del
  // contenedor con offset de 140px es la activa.
  useEffect(() => {
    const main = mainRef.current;
    if (!main) return;
    const handler = () => {
      for (const s of sections) {
        const el = document.getElementById(`sfl-${s.id}`);
        if (!el) continue;
        const r = el.getBoundingClientRect();
        if (r.top < 160 && r.bottom > 160) {
          setActive(s.id);
          break;
        }
      }
    };
    main.addEventListener('scroll', handler, { passive: true });
    handler();
    return () => main.removeEventListener('scroll', handler);
  }, [sections]);

  const scrollTo = (id: string) => {
    const el = document.getElementById(`sfl-${id}`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActive(id);
  };

  return (
    <div className={className} style={{ maxWidth: 1200, margin: '0 auto', padding: '20px 24px', fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{
            margin: 0, fontSize: 28, fontWeight: 900, letterSpacing: '-0.01em',
            fontFamily: "'Archivo', sans-serif", textTransform: 'uppercase', color: '#0A0A0A',
          }}>{title}</h1>
          {subtitle && (
            <div style={{ marginTop: 6, fontSize: 13, color: '#6B6660', fontFamily: "'JetBrains Mono', monospace', letterSpacing: '0.06em'" }}>
              {subtitle}
            </div>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 24, alignItems: 'start' }} className="sfl-grid">
        <aside style={{ position: 'sticky', top: 20 }}>
          <nav style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {sections.map((s) => {
              const isActive = active === s.id;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => scrollTo(s.id)}
                  style={{
                    width: '100%', textAlign: 'left',
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '10px 14px', fontSize: 12, fontWeight: 700,
                    color: isActive ? 'var(--brand, #0A0A0A)' : '#6B6660',
                    background: isActive ? 'rgba(0,0,0,0.04)' : 'transparent',
                    border: 'none', borderLeft: `3px solid ${isActive ? 'var(--brand, #B01820)' : 'transparent'}`,
                    borderRadius: 0, cursor: 'pointer',
                    fontFamily: "'Archivo', sans-serif",
                    letterSpacing: '0.08em', textTransform: 'uppercase',
                    transition: 'color 0.15s, background 0.15s',
                  }}
                >
                  {s.icon && <span aria-hidden style={{ fontSize: 14 }}>{s.icon}</span>}
                  <span style={{ flex: 1 }}>{s.label}</span>
                  {s.badge && <span>{s.badge}</span>}
                </button>
              );
            })}
          </nav>
          <div style={{ marginTop: 20 }}>{footerActions}</div>
        </aside>

        <div
          ref={mainRef}
          className="sfl-main"
          style={{
            display: 'flex', flexDirection: 'column', gap: 20,
            maxHeight, overflowY: 'auto', paddingRight: 4,
          }}
        >
          {sections.map((s) => (
            <section
              key={s.id}
              id={`sfl-${s.id}`}
              style={{
                background: '#FFFFFF', border: '1px solid #EAE6E1', borderRadius: 4,
                padding: '24px 28px', scrollMarginTop: 20,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, paddingBottom: 14, borderBottom: '1px solid #F5F0E8' }}>
                {s.icon && <span aria-hidden style={{ fontSize: 15 }}>{s.icon}</span>}
                <h2 style={{
                  margin: 0, fontSize: 12, fontWeight: 800,
                  textTransform: 'uppercase', letterSpacing: '0.14em', color: 'var(--brand, #B01820)',
                  fontFamily: "'Archivo', sans-serif",
                }}>
                  {s.label}
                </h2>
              </div>
              {s.content}
            </section>
          ))}
        </div>
      </div>

      <style>{`
        @media (max-width: 720px) {
          .sfl-grid { grid-template-columns: 1fr !important; }
          .sfl-main { max-height: none !important; }
        }
      `}</style>
    </div>
  );
}
